import type { ZonaAdminDto } from '@/api/administracio';

/** Format de versió de l'app: «2», «2.4» o «2.4.1». */
export const RE_VERSIO = /^\d+(\.\d+){0,2}$/;

export const versioValida = (v?: string | null) => !v || RE_VERSIO.test(v.trim());

/** Compara versions semàntiques («2.10.0» > «2.9.3»). Les parts que falten valen 0. */
export function comparaVersions(a: string, b: string): number {
  const pa = a.split('.').map((x) => Number.parseInt(x, 10) || 0);
  const pb = b.split('.').map((x) => Number.parseInt(x, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

/** La versió més alta d'una llista (o undefined si és buida). */
export function versioMaxima(versions: string[]): string | undefined {
  return versions.reduce<string | undefined>(
    (max, v) => (max == null || comparaVersions(v, max) > 0 ? v : max),
    undefined,
  );
}

/** ISO → valor d'un `<input type="datetime-local">` (hora local, sense segons). */
export function isoALocal(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Valor d'un `datetime-local` → ISO (o null si és buit). */
export function localAIso(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Ids de tots els descendents d'una zona (per evitar cicles en triar la zona superior). */
export function descendents(zones: ZonaAdminDto[], id: number): Set<number> {
  const out = new Set<number>();
  const pendents = [id];
  while (pendents.length) {
    const actual = pendents.pop() as number;
    for (const z of zones) {
      if (z.pareId === actual && z.id != null && !out.has(z.id)) {
        out.add(z.id);
        pendents.push(z.id);
      }
    }
  }
  return out;
}

export interface NodeZona {
  zona: ZonaAdminDto;
  nivell: number;
  fills: number;
}

/**
 * Zones en ordre d'arbre (pare abans dels fills, germans per nom). Les zones amb un pare que no
 * existeix es tracten com a arrels perquè no desapareguin.
 */
export function arbreZones(zones: ZonaAdminDto[]): NodeZona[] {
  const ids = new Set(zones.map((z) => z.id));
  const perNom = (a: ZonaAdminDto, b: ZonaAdminDto) => a.nom.localeCompare(b.nom);
  const fillsDe = (pare: number | null) =>
    zones
      .filter((z) => (pare == null ? z.pareId == null || !ids.has(z.pareId) : z.pareId === pare))
      .sort(perNom);
  const out: NodeZona[] = [];
  const vist = new Set<number | null | undefined>();
  const visita = (z: ZonaAdminDto, nivell: number) => {
    if (vist.has(z.id)) return;
    vist.add(z.id);
    const fills = z.id != null ? fillsDe(z.id) : [];
    out.push({ zona: z, nivell, fills: fills.length });
    fills.forEach((f) => visita(f, nivell + 1));
  };
  fillsDe(null).forEach((z) => visita(z, 0));
  // Zones dins d'un cicle (no haurien d'existir): es mostren igualment al final.
  zones.filter((z) => !vist.has(z.id)).forEach((z) => visita(z, 0));
  return out;
}
